import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    Avatar,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    Download as DownloadIcon,
    FileDownload as FileDownloadIcon,
    AddToDrive as AddToDriveIcon
} from "@mui/icons-material";
import { collection, getDocs, deleteDoc, doc, query, orderBy, addDoc } from "firebase/firestore";
import { db } from "../../../firebase/config";
import { useEventData } from "../EventDataContext";
import { deleteImage } from "../../../utils/imageUpload";
import Swal from "sweetalert2";
import JSZip from "jszip";
import { saveAs } from "file-saver";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const ActivityPage = () => {
    const { currentEventId, eventData } = useEventData();
    const [tabValue, setTabValue] = useState(0);
    const [wishes, setWishes] = useState([]);
    const [gifts, setGifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [previewImage, setPreviewImage] = useState(null);
    const [imgLoading, setImgLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [driveExporting, setDriveExporting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState("");

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const themeData = eventData?.theme || {};
    const primaryColor = "#8A5918";
    const secondaryColor = themeData.secondaryColor?.value || "#FFF3E0";

    const fetchActivities = useCallback(async () => {
        if (!currentEventId) return;

        setLoading(true);
        try {
            // Fetch Wishes
            const wishesRef = collection(db, "events", currentEventId, "wishes");
            const wishesQuery = query(wishesRef, orderBy("createdAt", "desc"));
            const wishesSnapshot = await getDocs(wishesQuery);
            const wishesList = wishesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt)
            }));
            setWishes(wishesList);

            // Fetch Gifts
            const giftsRef = collection(db, "events", currentEventId, "gifts");
            const giftsQuery = query(giftsRef, orderBy("createdAt", "desc"));
            const giftsSnapshot = await getDocs(giftsQuery);
            const giftsList = giftsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt)
            }));
            setGifts(giftsList);

        } catch (error) {
            console.error("Error fetching activities:", error);
            Swal.fire("Error", "Failed to load activities", "error");
        } finally {
            setLoading(false);
        }
    }, [currentEventId]);

    useEffect(() => {
        fetchActivities();
    }, [fetchActivities]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const createDriveFolder = async (name, parentId, accessToken) => {
        const metadata = {
            name,
            mimeType: 'application/vnd.google-apps.folder',
            parents: parentId ? [parentId] : []
        };

        const response = await fetch('https://www.googleapis.com/drive/v3/files?fields=id,webViewLink', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(metadata)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error?.message || 'Failed to create folder');
        }
        return await response.json();
    };

    const uploadDriveFile = async (blob, name, mimeType, parentId, accessToken) => {
        const metadata = {
            name,
            mimeType,
            parents: parentId ? [parentId] : []
        };

        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', blob);

        const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
            },
            body: form,
        });

        if (!response.ok) throw new Error('Failed to upload file');
        return await response.json();
    };

    const handleDelete = async (collectionName, id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    // Find the item to get the image URL
                    let itemToDelete;
                    if (collectionName === "wishes") {
                        itemToDelete = wishes.find(w => w.id === id);
                    } else {
                        itemToDelete = gifts.find(g => g.id === id);
                    }

                    // Delete image if it exists
                    if (itemToDelete) {
                        const imageUrl = collectionName === "wishes"
                            ? (itemToDelete.imageUrl || itemToDelete.image)
                            : itemToDelete.slipUrl;

                        if (imageUrl) {
                            await deleteImage(imageUrl);
                        }
                    }

                    await deleteDoc(doc(db, "events", currentEventId, collectionName, id));

                    if (collectionName === "wishes") {
                        setWishes(prev => prev.filter(item => item.id !== id));
                    } else {
                        setGifts(prev => prev.filter(item => item.id !== id));
                    }

                    Swal.fire("Deleted!", "Your file has been deleted.", "success");
                } catch (error) {
                    console.error("Error deleting document:", error);
                    Swal.fire("Error", "Failed to delete item", "error");
                }
            }
        });
    };

    const handleDownload = async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `slip_${Date.now()}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Download failed:", error);
            window.open(url, "_blank");
        }
    };

    const generateZipBlob = async () => {
        const zip = new JSZip();

        // Generate Wishes CSV
        const wishesHeader = ["Date", "Sender", "Message", "Image URL", "Image Filename"];
        const wishesRows = wishes.map(w => {
            const url = w.imageUrl || w.image;
            const filename = url ? `wish_${w.id}.jpg` : "";
            return [
                w.createdAt ? new Date(w.createdAt).toLocaleString() : "",
                `"${(w.sender || w.name || "Anonymous").replace(/"/g, '""')}"`,
                `"${(w.message || w.wish || "").replace(/"/g, '""')}"`,
                url || "",
                filename
            ];
        });
        const wishesCsv = [wishesHeader.join(","), ...wishesRows.map(r => r.join(","))].join("\n");
        zip.file("wishes.csv", wishesCsv);

        // Fetch and add Wish images
        const wishImgFolder = zip.folder("wishes_images");
        for (const w of wishes) {
            const url = w.imageUrl || w.image;
            if (url) {
                try {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    wishImgFolder.file(`wish_${w.id}.jpg`, blob);
                } catch (e) {
                    console.error("Failed to fetch wish image", e);
                }
            }
        }

        // Generate Gifts CSV
        const giftsHeader = ["Date", "Sender", "Slip URL", "Slip Filename"];
        const giftsRows = gifts.map(g => {
            const filename = g.slipUrl ? `gift_${g.id}.jpg` : "";
            return [
                g.createdAt ? new Date(g.createdAt).toLocaleString() : "",
                `"${(g.sender || g.name || "Anonymous").replace(/"/g, '""')}"`,
                g.slipUrl || "",
                filename
            ];
        });
        const giftsCsv = [giftsHeader.join(","), ...giftsRows.map(r => r.join(","))].join("\n");
        zip.file("gifts.csv", giftsCsv);

        // Fetch and add Gift images
        const giftImgFolder = zip.folder("gifts_images");
        for (const g of gifts) {
            if (g.slipUrl) {
                try {
                    const response = await fetch(g.slipUrl);
                    const blob = await response.blob();
                    giftImgFolder.file(`gift_${g.id}.jpg`, blob);
                } catch (e) {
                    console.error("Failed to fetch gift image", e);
                }
            }
        }

        return await zip.generateAsync({ type: "blob" });
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const content = await generateZipBlob();
            saveAs(content, `event_data_${currentEventId}.zip`);
        } catch (error) {
            console.error("Export failed", error);
            Swal.fire("Error", "Failed to export data", "error");
        } finally {
            setExporting(false);
        }
    };

    const handleDriveExport = async () => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!clientId) {
            Swal.fire({
                title: "Configuration Error",
                text: "Google Client ID is missing. Please add REACT_APP_GOOGLE_CLIENT_ID to your .env file.",
                icon: "error"
            });
            return;
        }

        setDriveExporting(true);
        setUploadProgress("Initializing...");

        try {
            // Load GIS script if needed
            if (!window.google) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement("script");
                    script.src = "https://accounts.google.com/gsi/client";
                    script.onload = resolve;
                    script.onerror = reject;
                    document.body.appendChild(script);
                });
            }

            const tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: clientId,
                scope: "https://www.googleapis.com/auth/drive.file",
                callback: async (tokenResponse) => {
                    if (tokenResponse.error) {
                        console.error("Token Error:", tokenResponse);
                        setDriveExporting(false);
                        setUploadProgress("");
                        return;
                    }

                    if (!window.google.accounts.oauth2.hasGrantedAllScopes(tokenResponse, 'https://www.googleapis.com/auth/drive.file')) {
                        Swal.fire("Error", "Permission to access Drive was denied. Please grant permission to save the file.", "error");
                        setDriveExporting(false);
                        setUploadProgress("");
                        return;
                    }

                    if (tokenResponse && tokenResponse.access_token) {
                        try {
                            const accessToken = tokenResponse.access_token;

                            // 1. Create Main Folder
                            setUploadProgress("Creating folder...");
                            const folderName = `Event Data - ${new Date().toLocaleDateString()} - ${currentEventId}`;
                            const mainFolder = await createDriveFolder(folderName, null, accessToken);

                            // 2. Generate and Upload CSVs
                            setUploadProgress("Uploading CSVs...");

                            // Wishes CSV
                            const wishesHeader = ["Date", "Sender", "Message", "Image URL", "Image Filename"];
                            const wishesRows = wishes.map(w => {
                                const url = w.imageUrl || w.image;
                                const filename = url ? `wish_${w.id}.jpg` : "";
                                return [
                                    w.createdAt ? new Date(w.createdAt).toLocaleString() : "",
                                    `"${(w.sender || w.name || "Anonymous").replace(/"/g, '""')}"`,
                                    `"${(w.message || w.wish || "").replace(/"/g, '""')}"`,
                                    url || "",
                                    filename
                                ];
                            });
                            const wishesCsv = [wishesHeader.join(","), ...wishesRows.map(r => r.join(","))].join("\n");
                            const wishesBlob = new Blob([wishesCsv], { type: 'text/csv' });
                            await uploadDriveFile(wishesBlob, 'wishes.csv', 'text/csv', mainFolder.id, accessToken);

                            // Gifts CSV
                            const giftsHeader = ["Date", "Sender", "Slip URL", "Slip Filename"];
                            const giftsRows = gifts.map(g => {
                                const filename = g.slipUrl ? `gift_${g.id}.jpg` : "";
                                return [
                                    g.createdAt ? new Date(g.createdAt).toLocaleString() : "",
                                    `"${(g.sender || g.name || "Anonymous").replace(/"/g, '""')}"`,
                                    g.slipUrl || "",
                                    filename
                                ];
                            });
                            const giftsCsv = [giftsHeader.join(","), ...giftsRows.map(r => r.join(","))].join("\n");
                            const giftsBlob = new Blob([giftsCsv], { type: 'text/csv' });
                            await uploadDriveFile(giftsBlob, 'gifts.csv', 'text/csv', mainFolder.id, accessToken);

                            // 3. Upload Wish Images
                            const wishesWithImages = wishes.filter(w => w.imageUrl || w.image);
                            if (wishesWithImages.length > 0) {
                                const wishesFolder = await createDriveFolder("Wishes Images", mainFolder.id, accessToken);
                                let count = 0;
                                for (const w of wishesWithImages) {
                                    const url = w.imageUrl || w.image;
                                    if (url) {
                                        try {
                                            count++;
                                            setUploadProgress(`Uploading Wish Image ${count}/${wishesWithImages.length}`);
                                            const imgResp = await fetch(url);
                                            const imgBlob = await imgResp.blob();
                                            await uploadDriveFile(imgBlob, `wish_${w.id}.jpg`, 'image/jpeg', wishesFolder.id, accessToken);
                                        } catch (e) {
                                            console.error("Failed to upload wish image", e);
                                        }
                                    }
                                }
                            }

                            // 4. Upload Gift Images
                            const giftsWithSlips = gifts.filter(g => g.slipUrl);
                            if (giftsWithSlips.length > 0) {
                                const giftsFolder = await createDriveFolder("Gifts Images", mainFolder.id, accessToken);
                                let count = 0;
                                for (const g of giftsWithSlips) {
                                    if (g.slipUrl) {
                                        try {
                                            count++;
                                            setUploadProgress(`Uploading Gift Slip ${count}/${giftsWithSlips.length}`);
                                            const imgResp = await fetch(g.slipUrl);
                                            const imgBlob = await imgResp.blob();
                                            await uploadDriveFile(imgBlob, `gift_${g.id}.jpg`, 'image/jpeg', giftsFolder.id, accessToken);
                                        } catch (e) {
                                            console.error("Failed to upload gift slip", e);
                                        }
                                    }
                                }
                            }

                            Swal.fire({
                                title: "Success",
                                text: "All files uploaded to Google Drive!",
                                icon: "success",
                                footer: `<a href="${mainFolder.webViewLink}" target="_blank">Open Folder in Drive</a>`
                            });

                        } catch (error) {
                            console.error("Drive upload failed", error);
                            Swal.fire("Error", `Failed to upload to Google Drive: ${error.message}`, "error");
                        } finally {
                            setDriveExporting(false);
                            setUploadProgress("");
                        }
                    } else {
                        setDriveExporting(false);
                        setUploadProgress("");
                    }
                },
            });

            tokenClient.requestAccessToken();
        } catch (error) {
            console.error("Drive init failed", error);
            Swal.fire("Error", "Failed to initialize Google Sign-In", "error");
            setDriveExporting(false);
            setUploadProgress("");
        }
    };

    const formatDate = (date) => {
        if (!date) return "-";
        return date.toLocaleString();
    };

    const currentData = tabValue === 0 ? wishes : gifts;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - currentData.length) : 0;

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="activity tabs"
                    TabIndicatorProps={{ style: { backgroundColor: primaryColor } }}
                    sx={{ '& .Mui-selected': { color: `${primaryColor} !important` } }}
                >
                    <Tab label={`Wishes (${wishes.length})`} />
                    <Tab label={`Gifts (${gifts.length})`} />
                </Tabs>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={exporting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <FileDownloadIcon />}
                        onClick={handleExport}
                        disabled={exporting || driveExporting || (wishes.length === 0 && gifts.length === 0)}
                        sx={{ bgcolor: primaryColor, color: '#fff', '&:hover': { bgcolor: primaryColor, opacity: 0.9 } }}
                    >
                        {exporting ? "Exporting..." : "Export Data"}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={driveExporting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <AddToDriveIcon />}
                        onClick={handleDriveExport}
                        disabled={exporting || driveExporting || (wishes.length === 0 && gifts.length === 0)}
                        sx={{ bgcolor: primaryColor, color: '#fff', '&:hover': { bgcolor: primaryColor, opacity: 0.9 } }}
                    >
                        {driveExporting ? (uploadProgress || "Uploading...") : "Save to Drive"}
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" p={5}>
                    <CircularProgress sx={{ color: primaryColor }} />
                </Box>
            ) : (
                <>
                    <TabPanel value={tabValue} index={0}>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="wishes table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Sender</TableCell>
                                        <TableCell>Message</TableCell>
                                        <TableCell>Image</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentData
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{formatDate(row.createdAt)}</TableCell>
                                                <TableCell>{row.sender || row.name || "Anonymous"}</TableCell>
                                                <TableCell>{row.message || row.wish || "-"}</TableCell>
                                                <TableCell>
                                                    {(row.imageUrl || row.image) ? (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<VisibilityIcon />}
                                                            onClick={() => {
                                                                setPreviewImage(row.imageUrl || row.image);
                                                                setImgLoading(true);
                                                            }}
                                                            sx={{ color: primaryColor, borderColor: primaryColor, '&:hover': { borderColor: primaryColor, bgcolor: secondaryColor } }}
                                                        >
                                                            View Image
                                                        </Button>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton color="error" onClick={() => handleDelete("wishes", row.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    {currentData.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">No wishes found</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="gifts table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Sender</TableCell>
                                        <TableCell>Slip</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentData
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>{formatDate(row.createdAt)}</TableCell>
                                                <TableCell>{row.sender || row.name || "Anonymous"}</TableCell>
                                                <TableCell>
                                                    {row.slipUrl ? (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<VisibilityIcon />}
                                                            onClick={() => {
                                                                setPreviewImage(row.slipUrl);
                                                                setImgLoading(true);
                                                            }}
                                                            sx={{ color: primaryColor, borderColor: primaryColor, '&:hover': { borderColor: primaryColor, bgcolor: secondaryColor } }}
                                                        >
                                                            View Slip
                                                        </Button>
                                                    ) : (
                                                        "No Slip"
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton color="error" onClick={() => handleDelete("gifts", row.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    {currentData.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">No gifts found</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={currentData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}

            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Preview</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => setPreviewImage(null)}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        {imgLoading && <CircularProgress />}
                        <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "70vh",
                                objectFit: "contain",
                                display: imgLoading ? "none" : "block"
                            }}
                            onLoad={() => setImgLoading(false)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button sx={{ color: primaryColor, '&:hover': { bgcolor: primaryColor, opacity: 0.5, color: '#fff' } }} onClick={() => setPreviewImage(null)}>Close</Button>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload(previewImage)}
                        sx={{ bgcolor: primaryColor, color: '#fff', '&:hover': { bgcolor: primaryColor, opacity: 0.9 } }}
                    >
                        Download
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ActivityPage;
